/**
 * LeftSidebar: BPM, offset, speed, step, snap steppers,
 * volume sliders, camera view, transport icons, save button.
 * Replaces old FileUpload sidebar. Matches Synth Riders editor layout.
 */

import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useEditorStore, stepsForType } from '../../store/editorStore';
import { exportBeatmap, exportEditedBeatmap, downloadBlob } from '../../api/client';
import { bakeDesignToObstacles } from '../../types/designShape';
import { buildExportDifficulties } from './exportPayload';
import ControlsPanel from '../ui/ControlsPanel';
import AutoBpmDialog from '../ui/AutoBpmDialog';
import { useAutoBpm } from '../ui/useAutoBpm';
import VolumeControls from '../ui/VolumeControls';
import TransportBar from '../ui/TransportBar';
import CollapsibleBox from './CollapsibleBox';
import ToolsBox from './ToolsBox';
import HistoryBox from './HistoryBox';
import type { PlaybackSpeed, CameraView } from '../../types/beatmap';
import { DEFAULT_VR_CAM_NAMES } from '../../types/beatmap';
import { setTickVolume, playStepChangeTick } from '../../audio/tickSound';
import { color, font } from '../ui/theme';
import {
  IcTrash, IcNote, IcClose, IcSettings,
} from '../ui/icons';
import GradientSlider from '../ui/GradientSlider';
import { BUILTIN_CAMERA_PRESETS, UNITY_DEFAULT_FOV } from '../three/TunnelScene';
import { groupStyle } from '../ui/editorSharedStyles';
import { detectSrBpm, detectSrOffset } from './srAudioDetect';
import { Tooltip, tip } from '../ui/Tooltip';
import MapGridButtons from '../ui/MapGridButtons';
import ExportVisualizerDialog, { type BundledVisualizer } from '../ui/ExportVisualizerDialog';
import EditorBackHeader from '../ui/EditorBackHeader';
import {
  sidebarStyle,
  cameraGridStyle,
  cameraBtnStyle,
  saveViewBtnStyle,
  bottomBtnsContainer,
  popupOverlayStyle,
  popupWindowStyle,
  popupTitleBarStyle,
  popupTitleTextStyle,
  popupCloseBtnStyle,
  popupBodyStyle,
  popupOptionBtnStyle,
  popupOptionIconStyle,
  popupOptionLabelStyle,
  popupOptionDescStyle,
  popupCancelBtnStyle,
} from './LeftSidebar.styles';

const SPEED_OPTIONS: PlaybackSpeed[] = [0.25, 0.5, 1, 2];
/** Pretty-print a beat-step as a fraction: 0.25→"1/4", 1/3→"1/3", 2/3→"2/3". */
function fmtStep(s: number): string {
  if (s >= 1) return String(s);
  const inv = 1 / s;
  const rInv = Math.round(inv);
  if (Math.abs(inv - rInv) < 1e-3) return `1/${rInv}`;
  for (let q = 2; q <= 24; q++) {
    const p = s * q;
    const rp = Math.round(p);
    if (rp >= 1 && Math.abs(p - rp) < 1e-3) return `${rp}/${q}`;
  }
  return s.toFixed(3);
}
const NJS_OPTIONS = [0.1, 0.2, 0.3, 0.4, 0.5, 1, 1.5, 2, 3, 4, 6, 8, 10, 12, 16, 20];

const CAMERA_VIEWS: { view: CameraView; label: string; icon: string }[] = [
  { view: 'default', label: 'Default', icon: '\u25CE' },
  { view: 'free', label: 'Free', icon: '\u2B21' },
  { view: 'custom', label: 'Custom', icon: '\u2606' },
];

// Render order: Default, Sides, Free, Custom
const CAMERA_ORDER: (CameraView | 'sides')[] = ['default', 'sides', 'free', 'custom'];

// Synth VR only: Default plus three free-look cams that remember their pose and carry a user-set name.
const VR_CAMERA_ORDER: CameraView[] = ['default', 'cam1', 'cam2', 'cam3'];

// Dynamic section label that reads title typography settings from the store
function SectionLabel({ text }: { text: string }) {
  const iface = useEditorStore(s => s.interfaceSettings);
  return (
    <div style={{
      color: iface.typoTitleColor,
      fontSize: iface.typoTitleSize,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 1.2,
      textAlign: 'center',
      marginBottom: iface.typoTitleMarginBottom,
      fontFamily: font.sans,
    }}>
      {text}
    </div>
  );
}

export default function LeftSidebar({ scrollRef }: { scrollRef?: React.Ref<HTMLDivElement> }) {
  const beatmap = useEditorStore(s => s.beatmap);
  const updateMeta = useEditorStore(s => s.updateMeta);
  const requestBpmChange = useEditorStore(s => s.requestBpmChange);
  const playbackSpeed = useEditorStore(s => s.playbackSpeed);
  const setPlaybackSpeed = useEditorStore(s => s.setPlaybackSpeed);
  const scrollStep = useEditorStore(s => s.scrollStep);
  const setScrollStep = useEditorStore(s => s.setScrollStep);
  const stepType = useEditorStore(s => s.stepType);
  const noteJumpSpeed = useEditorStore(s => s.noteJumpSpeed);
  const setNoteJumpSpeed = useEditorStore(s => s.setNoteJumpSpeed);
  const musicVolume = useEditorStore(s => s.musicVolume);
  const setMusicVolume = useEditorStore(s => s.setMusicVolume);
  const sfxVolume = useEditorStore(s => s.sfxVolume);
  const setSfxVolume = useEditorStore(s => s.setSfxVolume);
  const cameraView = useEditorStore(s => s.cameraView);
  const setCameraView = useEditorStore(s => s.setCameraView);
  const metronomeEnabled = useEditorStore(s => s.metronomeEnabled);
  const setMetronomeEnabled = useEditorStore(s => s.setMetronomeEnabled);
  const waveformVisible = useEditorStore(s => s.waveformVisible);
  const setWaveformVisible = useEditorStore(s => s.setWaveformVisible);
  const figureMode = useEditorStore(s => s.figureMode);
  const setFigureMode = useEditorStore(s => s.setFigureMode);
  const heatmapEnabled = useEditorStore(s => s.heatmapEnabled);
  const setHeatmapEnabled = useEditorStore(s => s.setHeatmapEnabled);
  const patternDifficultyVisible = useEditorStore(s => s.patternDifficultyVisible);
  const setPatternDifficultyVisible = useEditorStore(s => s.setPatternDifficultyVisible);
  const fovPanelOpen = useEditorStore(s => s.fovPanelOpen);
  const setFovPanelOpen = useEditorStore(s => s.setFovPanelOpen);
  const mirrorMode = useEditorStore(s => s.mirrorMode);
  const toggleMirrorMode = useEditorStore(s => s.toggleMirrorMode);
  const requestSaveCustomCamera = useEditorStore(s => s.requestSaveCustomCamera);
  const mapLocked = useEditorStore(s => s.mapLocked);
  const setActiveView = useEditorStore(s => s.setActiveView);
  const activeView = useEditorStore(s => s.activeView);
  const setFileManagerOpen = useEditorStore(s => s.setFileManagerOpen);
  const setNewMapOpen = useEditorStore(s => s.setNewMapOpen);
  const clearCurrentDifficulty = useEditorStore(s => s.clearCurrentDifficulty);
  const clearAllDifficulties = useEditorStore(s => s.clearAllDifficulties);
  const activeDifficulty = useEditorStore(s => s.activeDifficulty);
  const gridGuideVisible = useEditorStore(s => s.gridGuideVisible);
  const setGridGuideVisible = useEditorStore(s => s.setGridGuideVisible);
  const beatGridVisible = useEditorStore(s => s.interfaceSettings.beatGridVisible);
  const setInterfaceSettings = useEditorStore(s => s.setInterfaceSettings);
  const vrCams = useEditorStore(s => s.vrCams);
  const gameRailRendering = useEditorStore(s => s.gameRailRendering);
  const railSnapping = useEditorStore(s => s.railSnapping);
  const setRailSnapping = useEditorStore(s => s.setRailSnapping);
  const setGameRailRendering = useEditorStore(s => s.setGameRailRendering);
  const isVrSidebar = activeView === 'synth-rider';
  const cameraOrder = isVrSidebar ? VR_CAMERA_ORDER : CAMERA_ORDER;
  const [clearPopupOpen, setClearPopupOpen] = useState(false);
  const [autoOffsetBusy, setAutoOffsetBusy] = useState(false);

  // Auto BPM drives the shared metronome popup (detect → done).
  const autoBpm = useAutoBpm(detectSrBpm, useCallback((b: number) => requestBpmChange(b), [requestBpmChange]));

  const handleAutoOffset = useCallback(async () => {
    setAutoOffsetBusy(true);
    try { const off = await detectSrOffset(); updateMeta({ offset: off }); }
    catch (e) { alert((e as Error).message || 'Offset detection failed'); }
    finally { setAutoOffsetBusy(false); }
  }, [updateMeta]);

  const bpm = beatmap?.bpm || 120;
  const offset = beatmap?.offset || 0;

  // Every sidebar card uses the SHARED group-card style so all three editors
  // (Flow / Synth VR / Synth Console) look identical. Inter-card spacing comes
  // from the column's gap (sidebarStyle), not per-card margins.
  const uiBoxStyle = groupStyle;

  // ── Stepper Helpers ──────────────────────────────────────

  const cycleSpeed = useCallback((dir: 1 | -1) => {
    const idx = SPEED_OPTIONS.indexOf(playbackSpeed as PlaybackSpeed);
    const next = idx + dir;
    if (next >= 0 && next < SPEED_OPTIONS.length) {
      setPlaybackSpeed(SPEED_OPTIONS[next]);
    }
  }, [playbackSpeed, setPlaybackSpeed]);

  const cycleStep = useCallback((dir: 1 | -1) => {
    const opts = stepsForType(stepType);
    let idx = opts.indexOf(scrollStep);
    if (idx === -1) {   // ternary step not an exact array member → nearest
      let bd = Infinity;
      opts.forEach((o, i) => { const d = Math.abs(o - scrollStep); if (d < bd) { bd = d; idx = i; } });
    }
    const next = idx + dir;
    if (next >= 0 && next < opts.length) {
      setScrollStep(opts[next]);
      playStepChangeTick(dir > 0 ? 'up' : 'down');
    }
  }, [scrollStep, setScrollStep, stepType]);

  const cycleNJS = useCallback((dir: 1 | -1) => {
    const idx = NJS_OPTIONS.indexOf(noteJumpSpeed);
    const next = idx === -1 ? NJS_OPTIONS.indexOf(2) + dir : idx + dir;
    if (next >= 0 && next < NJS_OPTIONS.length) {
      setNoteJumpSpeed(NJS_OPTIONS[next]);
    }
  }, [noteJumpSpeed, setNoteJumpSpeed]);

  const handleSfxVolumeChange = useCallback((vol: number) => {
    setSfxVolume(vol);
    setTickVolume(vol);
  }, [setSfxVolume]);

  // ── Save (project) + Export (file) ──────────────────────────────

  const [exportOpen, setExportOpen] = useState(false);

  // Save = persist project changes to the per-map draft (manual autosave). No
  // download — that's Export. The "Project saved" toast is rendered by App
  // (anchored above the playback bar), driven by this store flag.
  const handleSave = useCallback(() => {
    if (!beatmap) return;
    const st = useEditorStore.getState();
    st.saveProjectDraft();
    st.setProjectSaved(true);
    window.setTimeout(() => useEditorStore.getState().setProjectSaved(false), 1600);
  }, [beatmap]);

  // Export = write the shareable .synth, optionally embedding visualizer(s).
  const handleExport = useCallback(async (visualizers: BundledVisualizer[] = []) => {
    if (!beatmap) return;
    setExportOpen(false);
    try {
      const password = mapLocked ? 'hC2*wE5R*qQzv@a!' : undefined;
      const store = useEditorStore.getState();

      // Bake the decorative Shape Designer shapes into real .synth obstacles
      // (Squares/Triangles/Slides) so they persist in the file + the game can
      // show them. Design is per-map (global across difficulties).
      // The bake is added to each difficulty's own squares/triangles, never substituted for them.
      const baked = bakeDesignToObstacles(store.designShapes, beatmap.bpm);
      const difficulties = buildExportDifficulties(beatmap.difficulties, baked);

      const blob = await exportEditedBeatmap({
        id: beatmap.id, name: beatmap.name, author: beatmap.author, beatmapper: beatmap.beatmapper,
        bpm: beatmap.bpm, offset: beatmap.offset, difficulties,
        bookmarks: store.bookmarks, flash_events: store.flashEvents,
        // Flash effects per difficulty (Synth VR only; Console never authors them, so its export keeps the file's).
        // Draft Mode likewise (on unless the mapper turned it off).
        ...(store.activeView === 'synth-rider' ? {
          effects: store.effects, draft_mode: beatmap.draft_mode ?? true,
          custom_difficulty_name: beatmap.custom_difficulty_name ?? 'Custom', custom_difficulty_speed: beatmap.custom_difficulty_speed ?? 1,
        } : {}),
        // Embed the full Shape Designer groups so re-importing restores the art.
        decor_walls: store.designShapes,
        // Visualizers chosen in the pre-export popup → embedded at the ZIP root.
        bundled_files: visualizers.map(v => ({ name: v.name, content: v.json })),
        // Synth VR writes the desktop editor's file; Console keeps its own export.
        desktop_format: store.activeView === 'synth-rider',
      }, password);
      downloadBlob(blob, `${beatmap.name}.synth`);
    } catch (e: any) {
      console.error('Export failed:', e);
    }
  }, [beatmap, mapLocked]);

  // ── Ctrl+S → project save (not export) ──
  useEffect(() => {
    const onSaveMap = () => handleSave();
    window.addEventListener('abm-save-map', onSaveMap);
    return () => window.removeEventListener('abm-save-map', onSaveMap);
  }, [handleSave]);

  // ── Render ──────────────────────────────────────────────

  const mapButtons = (
    <MapGridButtons
      onNew={() => setNewMapOpen(true)}
      onOpen={() => setFileManagerOpen(true)}
      onSave={handleSave}
      onExport={() => setExportOpen(true)}
      onClear={() => setClearPopupOpen(true)}
    />
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Top header: circular Back + accent pill. OUTSIDE the scroll element so
          the scrollbar range + edge fade leave the editor logo alone. */}
      <div style={{ flexShrink: 0, padding: '0 10px 0 11px' }}>
        <EditorBackHeader name={activeView === 'console' ? 'Synth Console' : 'Synth VR'} onBack={() => setActiveView('dashboard')} />
      </div>
      <div ref={scrollRef} className="overlay-scroll" style={sidebarStyle}>
      {/* BPM + Offset + Speed + Step + NJS */}
      <CollapsibleBox id="Controls" title="Controls" boxStyle={uiBoxStyle} defaultSidebar="left">
        <ControlsPanel
          steppers={[
            { label: 'BPM', value: bpm.toString(), editable: true,
              // Synth VR commits a typed BPM on Enter or blur, because each change may ask how the objects follow it.
              // Synth VR has no arrows on the BPM: it is typed, and the change goes through the dialog.
              ...(isVrSidebar
                ? { hideArrows: true, onCommitValue: (raw: string) => { const b = parseFloat(raw); if (b > 0) requestBpmChange(b); }, onDec: () => {}, onInc: () => {} }
                : {
                  onValueChange: (raw: string) => updateMeta({ bpm: parseFloat(raw) || 120 }),
                  onDec: () => updateMeta({ bpm: Math.max(1, bpm - 1) }), onInc: () => updateMeta({ bpm: bpm + 1 }),
                }) },
            { label: 'Offset', value: `${offset.toFixed(0)}`, editable: true,  // offset is in ms
              onValueChange: (raw) => updateMeta({ offset: parseFloat(raw) || 0 }),
              onDec: () => updateMeta({ offset: offset - 5 }), onInc: () => updateMeta({ offset: offset + 5 }) },
            { label: 'Speed', value: `${playbackSpeed}x`, onDec: () => cycleSpeed(-1), onInc: () => cycleSpeed(1) },
            { label: 'Step', value: fmtStep(scrollStep) + (stepType === 'ternary' ? 'T' : stepType === 'all' ? '·' : ''), onDec: () => cycleStep(-1), onInc: () => cycleStep(1) },
            { label: 'NJS', value: `${noteJumpSpeed}x`, onDec: () => cycleNJS(-1), onInc: () => cycleNJS(1) },
          ]}
          onAutoBpm={autoBpm.run}
          autoBpmBusy={autoBpm.busy}
          onAutoOffset={handleAutoOffset}
          autoOffsetBusy={autoOffsetBusy}
          autoDisabled={!beatmap}
        />
      </CollapsibleBox>
      <AutoBpmDialog
        stage={autoBpm.stage}
        detected={autoBpm.detected}
        final={autoBpm.detected}
        onChoose={autoBpm.choose}
        onClose={autoBpm.dismiss}
      />

      {/* Volume Controls — each slider in its own pill container, like Flow. */}
      <CollapsibleBox id="Volume" title="Volume" boxStyle={uiBoxStyle} defaultSidebar="left">
        <VolumeControls music={musicVolume} sfx={sfxVolume} onMusic={setMusicVolume} onSfx={handleSfxVolumeChange} />
      </CollapsibleBox>

      {/* Camera View — the ⚙ opens the live pose controls for whichever view is
          active (reviewer: "custom camera has no visible controls"). */}
      <CollapsibleBox
        id="Camera View"
        title="Camera View"
        boxStyle={uiBoxStyle}
        defaultSidebar="left"
        headerAction={<CameraOptionsButton />}
      >
      <div style={cameraGridStyle}>
        {cameraOrder.map(key => {
          if (key === 'sides') {
            const isSide = cameraView === 'left' || cameraView === 'right';
            return (
              <button
                key="sides"
                onClick={() => {
                  if (cameraView === 'left') setCameraView('right');
                  else setCameraView('left');
                }}
                style={{
                  ...cameraBtnStyle,
                  background: isSide
                    ? 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.25), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.15))'
                    : 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
                  borderColor: isSide ? 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)' : 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.12)',
                  color: isSide ? color.accent : color.inkFaint,
                  boxShadow: 'none',
                }}
                title={undefined}
              >
                <span className="ic-themed" style={{ fontSize: 12, display: 'flex', gap: 2 }}>
                  <span style={{ opacity: cameraView === 'left' ? 1 : 0.3 }}>{'\u25C0'}</span>
                  <span style={{ opacity: cameraView === 'right' ? 1 : 0.3 }}>{'\u25B6'}</span>
                </span>
                <span style={{ fontSize: 9 }}>Sides</span>
              </button>
            );
          }
          const vrSlot = key === 'cam1' ? 0 : key === 'cam2' ? 1 : key === 'cam3' ? 2 : -1;
          const cv = vrSlot >= 0
            ? { view: key as CameraView, label: vrCams[vrSlot].name, icon: '⬡' }
            : CAMERA_VIEWS.find(c => c.view === key)!;
          const active = cameraView === cv.view;
          return (
            <button
              key={cv.view}
              onClick={() => setCameraView(cv.view)}
              style={{
                ...cameraBtnStyle,
                background: active
                  ? 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.25), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.15))'
                  : 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
                borderColor: active ? 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)' : 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.12)',
                color: active ? color.accent : color.inkFaint,
                boxShadow: 'none',
              }}
            >
              <span className="ic-themed" style={{ fontSize: 12 }}>{cv.icon}</span>
              <span style={{ fontSize: 9, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cv.label}</span>
            </button>
          );
        })}
      </div>

      {/* Save / Clear custom camera */}
      {cameraView === 'free' && (
        <Tooltip content={tip({ title: 'Save Camera View', body: "Saves the current free-look camera angle as your Custom preset. Switch to Custom anytime to return to this exact position." })}>
          <button onClick={requestSaveCustomCamera} style={saveViewBtnStyle}>
            {'\uD83D\uDCBE'} Save View
          </button>
        </Tooltip>
      )}
      <CameraOptionsPanel />
      </CollapsibleBox>{/* end Camera View */}

      {/* Tools — Synth VR only: Rail Smoothing, Note Rotation and Scaling (SRVR-0351). */}
      {isVrSidebar && (
        <CollapsibleBox id="Tools" title="Tools" boxStyle={uiBoxStyle} defaultSidebar="left" placeAfter="Camera View">
          <ToolsBox />
        </CollapsibleBox>
      )}

      {/* History — Synth VR only: the last edit and the undo history behind it (SRVR-0357). */}
      {isVrSidebar && (
        <CollapsibleBox id="History" title="History" boxStyle={uiBoxStyle} defaultSidebar="left" placeAfter="Tools">
          <HistoryBox />
        </CollapsibleBox>
      )}

      {/* Transport Icons */}
      <CollapsibleBox id="Transport" title="Transport" boxStyle={uiBoxStyle} defaultSidebar="left">
        {/* Editor Settings opens from the top-right header button instead. */}
        <TransportBar
          items={[
            { id: 'grid', active: gridGuideVisible, onClick: () => setGridGuideVisible(!gridGuideVisible) },
            { id: 'beatgrid', active: beatGridVisible, onClick: () => setInterfaceSettings({ beatGridVisible: !beatGridVisible }) },
            { id: 'metronome', active: metronomeEnabled, onClick: () => setMetronomeEnabled(!metronomeEnabled) },
            { id: 'waveform', active: waveformVisible, onClick: () => setWaveformVisible(!waveformVisible) },
            { id: 'mannequin', active: figureMode !== 'off', onClick: () => setFigureMode(figureMode === 'off' ? 'mannequin' : 'off') },
            { id: 'heatmap', active: heatmapEnabled, onClick: () => setHeatmapEnabled(!heatmapEnabled) },
            { id: 'difficulty', active: patternDifficultyVisible, onClick: () => setPatternDifficultyVisible(!patternDifficultyVisible) },
            { id: 'render', active: fovPanelOpen, onClick: () => setFovPanelOpen(!fovPanelOpen) },
            { id: 'mirror', active: mirrorMode, onClick: () => toggleMirrorMode() },
            // Synth VR only: rails in their in-game shape (SRVR-0356).
            ...(isVrSidebar ? [{ id: 'railrender', ariaLabel: 'Rail Rendering', active: gameRailRendering, onClick: () => setGameRailRendering(!gameRailRendering) }] : []),
            // Synth VR only: Snap to Rail, Alt+F3.
            ...(isVrSidebar ? [{ id: 'snaprail', ariaLabel: 'Snap to Rail', active: railSnapping, onClick: () => setRailSnapping(!railSnapping) }] : []),
          ]}
        />
      </CollapsibleBox>{/* end Transport */}

      {/* Map actions - standard New/Open/Save/Clear grid (shared with Flow). Synth VR gives them a File
          box like every other, so it collapses, reorders and floats (SRVR-0363); Console keeps them
          untitled and pinned to the bottom. */}
      {isVrSidebar ? (
        <CollapsibleBox id="File" title="File" boxStyle={uiBoxStyle} defaultSidebar="left" placeAfter="Transport">
          <div style={bottomBtnsContainer}>{mapButtons}</div>
        </CollapsibleBox>
      ) : (
        <div style={{ ...uiBoxStyle, ...bottomBtnsContainer, order: 999 }}>{mapButtons}</div>
      )}

      {/* Export dialog (portal-based, like Clear Map below). Rendered inline it sat inside the
          sidebar's masked scroll container, which clipped the fixed-position dialog to the sidebar
          strip — Export appeared to do nothing (CAND-AC). */}
      {ReactDOM.createPortal(
        <ExportVisualizerDialog
          open={exportOpen}
          mapName={beatmap?.name}
          onCancel={() => setExportOpen(false)}
          onConfirm={handleExport}
        />,
        document.body,
      )}
      {/* "Project saved" toast is rendered by App, anchored above the playback bar. */}
      {/* Back moved to the top header (EditorBackHeader) — shared with all editors. */}

      {/* Clear Map Popup (portal-based — unaffected by the scroll/mask) */}
      {clearPopupOpen && (
        <ClearMapPopup
          activeDifficulty={activeDifficulty}
          onClearCurrent={() => { clearCurrentDifficulty(); setClearPopupOpen(false); }}
          onClearAll={() => { clearAllDifficulties(); setClearPopupOpen(false); }}
          onCancel={() => setClearPopupOpen(false)}
        />
      )}
      </div>
    </div>
  );
}

function Divider() {
  return (
    <div style={{
      height: 1,
      background: 'linear-gradient(90deg, transparent, oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.2), transparent)',
      margin: '8px 0',
    }} />
  );
}

// ── Camera options (⚙ in the Camera View header) ─────────────────────────────
//
// Reviewer: "custom camera has no visible controls". The Settings dialog could
// already nudge each preset's pose, but nothing in the panel said so. This is
// the same shape as the Minimap's ⚙: a gear in the header opens live sliders
// for the ACTIVE view, so what you drag is what you see.

/** ⚙ button — opens/closes the camera pose controls. */
function CameraOptionsButton() {
  const open = useEditorStore(s => s.cameraOptionsOpen);
  const setOpen = useEditorStore(s => s.setCameraOptionsOpen);
  return (
    <button
      type="button"
      aria-label="Camera options"
      title="Camera options — height, distance, tilt, FOV and travel speed for the active view"
      onClick={() => setOpen(!open)}
      style={{ ...camGearBtnStyle, ...(open ? camGearBtnActiveStyle : null) }}
    >
      <IcSettings size={13} />
    </button>
  );
}

/** Live pose sliders for whichever camera view is active. */
function CameraOptionsPanel() {
  const open = useEditorStore(s => s.cameraOptionsOpen);
  const setOpen = useEditorStore(s => s.setCameraOptionsOpen);
  const view = useEditorStore(s => s.cameraView);
  const iface = useEditorStore(s => s.interfaceSettings);
  const setIface = useEditorStore(s => s.setInterfaceSettings);
  const defPos = useEditorStore(s => s.defaultCameraPosition);
  const defRot = useEditorStore(s => s.defaultCameraRotation);
  const cusPos = useEditorStore(s => s.customCameraPosition);
  const cusRot = useEditorStore(s => s.customCameraRotation);
  const setDefPos = useEditorStore(s => s.setDefaultCameraPosition);
  const setDefRot = useEditorStore(s => s.setDefaultCameraRotation);
  const activeView = useEditorStore(s => s.activeView);
  const vrCams = useEditorStore(s => s.vrCams);
  const setVrCamName = useEditorStore(s => s.setVrCamName);
  const setCusPos = useEditorStore(s => s.setCustomCameraPosition);
  const setCusRot = useEditorStore(s => s.setCustomCameraRotation);
  const clearCustom = useEditorStore(s => s.clearCustomCamera);

  if (!open) return null;

  // Synth VR: name the three custom cams.
  const isVrPanel = activeView === 'synth-rider';
  const vrNames = isVrPanel ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
      {vrCams.map((cam, i) => (
        <input
          key={i}
          type="text"
          value={cam.name}
          maxLength={12}
          aria-label={`Name of custom camera ${i + 1}`}
          onChange={e => setVrCamName(i, e.target.value)}
          onBlur={e => { if (!e.target.value.trim()) setVrCamName(i, DEFAULT_VR_CAM_NAMES[i]); }}
          onKeyDown={e => {
            e.stopPropagation();
            if (e.key === 'Enter') {
              if (!e.currentTarget.value.trim()) setVrCamName(i, DEFAULT_VR_CAM_NAMES[i]);
              setOpen(false);
            }
          }}
          style={camNameInputStyle}
        />
      ))}
    </div>
  ) : null;
  if (isVrPanel && view !== 'default') {
    return (
      <div>
        {vrNames}
        <div style={camHintStyle}>
          Rename the custom cameras above. Each one flies like a free camera (right-drag to look,
          WASD/QE to fly, middle-drag to pan, Shift+wheel to dolly) and remembers where you left it.
        </div>
      </div>
    );
  }

  // Free view has no fixed pose to edit — it's driven by the mouse.
  if (view === 'free') {
    return (
      <div style={camHintStyle}>
        Free look is driven by the mouse: right-drag to look, WASD/QE to fly,
        middle-drag to pan, wheel to move along the timeline (Shift+wheel dollies).
        Use “Save View” to keep this angle as your Custom camera.
      </div>
    );
  }
  if (view === 'left' || view === 'right') {
    return (
      <div style={camHintStyle}>
        The side views are fixed presets. Switch to Default or Custom to adjust a
        pose here, or fine-tune every preset in Settings → Camera.
      </div>
    );
  }

  const isCustom = view === 'custom';
  const pos = (isCustom ? cusPos : defPos) ?? BUILTIN_CAMERA_PRESETS.default.position;
  const rot = (isCustom ? cusRot : defRot) ?? BUILTIN_CAMERA_PRESETS.default.rotation;
  const setPos = (p: [number, number, number]) => (isCustom ? setCusPos(p) : setDefPos(p));
  const setRot = (r: [number, number, number]) => (isCustom ? setCusRot(r) : setDefRot(r));

  const tiltDeg = (rot[0] * 180) / Math.PI;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 6 }}>
      {vrNames}
      <GradientSlider inline label="Height" valueText={pos[1].toFixed(2)}
        value={(pos[1] - 0.5) / 3}
        onChange={v => setPos([pos[0], Math.round((0.5 + v * 3) * 100) / 100, pos[2]])} />
      <GradientSlider inline label="Distance" valueText={pos[2].toFixed(2)}
        value={(pos[2] - 0.5) / 7.5}
        onChange={v => setPos([pos[0], pos[1], Math.round((0.5 + v * 7.5) * 100) / 100])} />
      <GradientSlider inline label="Tilt" valueText={`${tiltDeg.toFixed(0)}°`}
        value={(tiltDeg + 30) / 60}
        onChange={v => { const d = -30 + v * 60; setRot([Math.round(d) * Math.PI / 180, rot[1], rot[2]]); }} />
      <GradientSlider inline label="FOV" valueText={`${iface.cameraFov.toFixed(0)}°`}
        value={(iface.cameraFov - 30) / 90}
        onChange={v => setIface({ cameraFov: Math.round(30 + v * 90) })} />
      <GradientSlider inline label="Travel" valueText={(iface.cameraTravelSpeed ?? 6) === 0 ? 'Instant' : String(iface.cameraTravelSpeed ?? 6)}
        value={(iface.cameraTravelSpeed ?? 6) / 10}
        onChange={v => setIface({ cameraTravelSpeed: Math.round(v * 10) })} />
      <button
        type="button"
        style={camResetBtnStyle}
        onClick={() => {
          if (isCustom) { clearCustom(); return; }
          const d = BUILTIN_CAMERA_PRESETS.default;
          setDefPos([...d.position] as [number, number, number]);
          setDefRot([...d.rotation] as [number, number, number]);
          setIface({ cameraFov: UNITY_DEFAULT_FOV });
        }}
      >
        Reset to the desktop-editor view
      </button>
    </div>
  );
}

const camGearBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  width: 22, height: 22, padding: 0, borderRadius: 7, cursor: 'pointer',
  fontFamily: font.sans,
  background: 'oklch(0.20 calc(0.028 * var(--tint-c)) var(--tint-h) / 0.55)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.3)',
  color: color.accentSoft,
  transition: 'all 0.15s ease',
};
const camGearBtnActiveStyle: React.CSSProperties = {
  background: 'oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.18)',
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)',
  color: 'var(--accent)',
};
const camNameInputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '4px 8px', borderRadius: 7,
  fontFamily: font.sans, fontSize: 10, color: color.ink,
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.6)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.3)',
  outline: 'none',
};
const camHintStyle: React.CSSProperties = {
  marginTop: 6, fontSize: 9, lineHeight: 1.45,
  color: color.inkFaint, fontFamily: font.sans,
};
const camResetBtnStyle: React.CSSProperties = {
  marginTop: 4, padding: '6px 10px', borderRadius: 999,
  fontFamily: font.sans, fontSize: 9, fontWeight: 600, letterSpacing: 0.4,
  cursor: 'pointer', color: color.accentSoft,
  background: 'oklch(0.20 calc(0.028 * var(--tint-c)) var(--tint-h) / 0.6)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.3)',
};

// ── Clear Map Popup (portal-based, Settings-style window) ─────────────────────

function ClearMapPopup({
  activeDifficulty,
  onClearCurrent,
  onClearAll,
  onCancel,
}: {
  activeDifficulty: string;
  onClearCurrent: () => void;
  onClearAll: () => void;
  onCancel: () => void;
}) {
  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return ReactDOM.createPortal(
    <div style={popupOverlayStyle} onClick={onCancel}>
      <div style={popupWindowStyle} onClick={e => e.stopPropagation()}>
        {/* Title bar — matches Settings dialog */}
        <div style={popupTitleBarStyle}>
          <div style={popupTitleTextStyle}>
            <IcTrash size={14} />
            <span>Clear Map</span>
          </div>
          <button style={popupCloseBtnStyle} onClick={onCancel}>
            <IcClose size={14} />
          </button>
        </div>

        {/* Body */}
        <div style={popupBodyStyle}>
          <button onClick={onClearCurrent} style={popupOptionBtnStyle}
            onMouseEnter={e => { e.currentTarget.style.background = 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.12)'; e.currentTarget.style.borderColor = 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.4)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(15, 15, 30, 0.6)'; e.currentTarget.style.borderColor = 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.2)'; }}
          >
            <span style={popupOptionIconStyle}><IcNote size={22} /></span>
            <span style={popupOptionLabelStyle}>Clear Current Difficulty</span>
            <span style={popupOptionDescStyle}>Remove all notes, rails, slides from "{activeDifficulty}"</span>
          </button>

          <button onClick={onClearAll} style={{ ...popupOptionBtnStyle, borderColor: 'rgba(239, 68, 68, 0.3)' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'; e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.5)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(15, 15, 30, 0.6)'; e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)'; }}
          >
            <span style={popupOptionIconStyle}><IcTrash size={22} /></span>
            <span style={popupOptionLabelStyle}>Clear All Difficulties</span>
            <span style={popupOptionDescStyle}>Remove all notes, rails, slides from every difficulty</span>
          </button>

          <button onClick={onCancel} style={popupCancelBtnStyle}
            onMouseEnter={e => { e.currentTarget.style.background = 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.08)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

